import { useState, useEffect, useCallback } from 'react';
import { ChevronLeft, ChevronRight, Filter, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { cn } from '@/lib/utils';
import { api } from '@/lib/api';

export interface FilterValues {
  search: string;
  status_code: string;
  city: string;
  service_type: string;
  driver_id: string;
  carrier_id: string;
  created_from: string;
  created_to: string;
}

interface ShipmentFilterSidebarProps {
  filters: FilterValues;
  onFilterChange: (key: keyof FilterValues, value: string) => void;
  onClearFilters: () => void;
  className?: string;
}

interface LookupOption {
  value: string;
  label: string;
}

export function ShipmentFilterSidebar({
  filters,
  onFilterChange,
  onClearFilters,
  className,
}: ShipmentFilterSidebarProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  
  const [statusOptions, setStatusOptions] = useState<LookupOption[]>([]);
  const [cityOptions, setCityOptions] = useState<LookupOption[]>([]);
  const [serviceTypeOptions, setServiceTypeOptions] = useState<LookupOption[]>([]);
  const [driverOptions, setDriverOptions] = useState<LookupOption[]>([]);
  const [carrierOptions, setCarrierOptions] = useState<LookupOption[]>([]);

  const fetchLookupData = useCallback(async () => {
    // NOTE: don't let one failing endpoint block the others (common cause of empty dropdowns)
    const results = await Promise.allSettled([
      api.get('/api/v1/lookups/shipment-statuses?lang=ar'),
      api.get('/api/v1/lookups/cities?lang=ar'),
      api.get('/api/v1/lookups/shipment-types?lang=ar'),
      api.get('/api/v1/dashboard/drivers', { page: 1, limit: 100 }),
      // Carriers list exists here in this app
      api.get('/api/v1/carriers', { page: 1, limit: 100 }),
    ]);

    const getItems = (res: any): any[] => {
      // api.get returns ApiResponse -> { success, message, data: ... }
      const root = res?.data ?? res;
      return root?.items ?? root?.data?.items ?? root?.data?.data?.items ?? [];
    };

    const getList = (res: any): any[] => {
      const root = res?.data ?? res;
      const list =
        root?.list ??
        root?.data?.list ??
        root?.data?.data?.list ??
        (Array.isArray(root) ? root : undefined) ??
        (Array.isArray(root?.data) ? root.data : undefined) ??
        [];
      return Array.isArray(list) ? list : [];
    };

    const toLookupOptions = (items: any[]): LookupOption[] =>
      items
        .map((item) => ({ value: String(item.value ?? ''), label: String(item.label ?? '') }))
        .filter((o) => o.value && o.label);

    const toEntityOptions = (list: any[]): LookupOption[] =>
      list
        .map((item) => ({
          value: String(item.id ?? ''),
          label: String(item.name ?? item.full_name ?? item.label ?? ''),
        }))
        .filter((o) => o.value && o.label);

    if (results[0].status === 'fulfilled') setStatusOptions(toLookupOptions(getItems(results[0].value)));
    else console.error('Lookup shipment-statuses failed', results[0].reason);

    if (results[1].status === 'fulfilled') setCityOptions(toLookupOptions(getItems(results[1].value)));
    else console.error('Lookup cities failed', results[1].reason);

    if (results[2].status === 'fulfilled') setServiceTypeOptions(toLookupOptions(getItems(results[2].value)));
    else console.error('Lookup service-types failed', results[2].reason);

    if (results[3].status === 'fulfilled') setDriverOptions(toEntityOptions(getList(results[3].value)));
    else console.error('Drivers list failed', results[3].reason);

    if (results[4].status === 'fulfilled') setCarrierOptions(toEntityOptions(getList(results[4].value)));
    else console.error('Carriers list failed', results[4].reason);
  }, []);

  useEffect(() => {
    fetchLookupData();
  }, [fetchLookupData]);

  const hasActiveFilters = Object.entries(filters).some(([_, value]) => {
    return value !== '';
  });

  return (
    <div
      className={cn(
        'relative transition-all duration-300 ease-in-out',
        isCollapsed ? 'w-12' : 'w-52',
        className
      )}
    >
      {isCollapsed ? (
        <div className="flex h-full flex-col items-center pt-4">
          <Button
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={() => setIsCollapsed(false)}
          >
            <ChevronLeft className="h-3 w-3" />
          </Button>
          <Filter className="h-5 w-5 text-muted-foreground mt-2" />
          {hasActiveFilters && (
            <div className="mt-2 h-2 w-2 rounded-full bg-primary" />
          )}
        </div>
      ) : (
        <div className="space-y-2 pe-2 h-full overflow-y-auto pb-4">
          {/* Header */}
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold flex items-center gap-2">
              <Filter className="h-4 w-4" />
              فلترة
            </h3>
            <div className="flex items-center gap-1">
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 text-xs text-destructive hover:text-destructive px-2"
                  onClick={onClearFilters}
                >
                  مسح
                </Button>
              )}
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={() => setIsCollapsed(true)}
              >
                <ChevronRight className="h-3 w-3" />
              </Button>
            </div>
          </div>

          {/* Search Input */}
          <div className="relative">
            <Search className="absolute end-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              type="text"
              placeholder="رقم التتبع"
              value={filters.search}
              onChange={(e) => onFilterChange('search', e.target.value)}
              className="h-9 text-xs pe-8"
            />
          </div>

          {/* Status Filter */}
          <SearchableSelect
            options={statusOptions}
            value={filters.status_code}
            onValueChange={(v) => onFilterChange('status_code', v)}
            placeholder="الحالة"
            searchPlaceholder="بحث في الحالات..."
          />

          {/* City Filter */}
          <SearchableSelect
            options={cityOptions}
            value={filters.city}
            onValueChange={(v) => onFilterChange('city', v)}
            placeholder="المدينة"
            searchPlaceholder="بحث في المدن..."
          />

          {/* Service Type Filter */}
          <SearchableSelect
            options={serviceTypeOptions}
            value={filters.service_type}
            onValueChange={(v) => onFilterChange('service_type', v)}
            placeholder="نوع الخدمة"
            searchPlaceholder="بحث..."
          />

          {/* Driver Filter */}
          <SearchableSelect
            options={driverOptions}
            value={filters.driver_id}
            onValueChange={(v) => onFilterChange('driver_id', v)}
            placeholder="السائق"
            searchPlaceholder="بحث في السائقين..."
          />

          {/* Carrier Filter */}
          <SearchableSelect
            options={carrierOptions}
            value={filters.carrier_id}
            onValueChange={(v) => onFilterChange('carrier_id', v)}
            placeholder="الناقل"
            searchPlaceholder="بحث في الناقلين..."
          />

          {/* Date From */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">من تاريخ</label>
            <Input
              type="date"
              value={filters.created_from}
              onChange={(e) => onFilterChange('created_from', e.target.value)}
              className="h-9 text-xs"
            />
          </div>

          {/* Date To */}
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">إلى تاريخ</label>
            <Input
              type="date"
              value={filters.created_to}
              onChange={(e) => onFilterChange('created_to', e.target.value)}
              className="h-9 text-xs"
            />
          </div>
        </div>
      )}
    </div>
  );
}
